import { NavLink } from "react-router-dom";
import { Home, Building2, Workflow, Activity, Plus, FileCode, KeyRound, Globe } from "lucide-react";
import { cn } from "../../utils/cn";
import { useAuthUser } from "../../hooks/useAuthUser";
import type { WosRole } from "../../types/auth";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
  roles?: WosRole[]; // se ausente, qualquer autenticado vê
}

const NAV_MAIN: NavItem[] = [
  { to: "/", label: "Dashboard", icon: Home, end: true },
  { to: "/tenants", label: "Tenants", icon: Building2 },
  { to: "/sagas", label: "Sagas", icon: Workflow },
  { to: "/sagas/definitions", label: "Definitions", icon: FileCode },
];

const NAV_ACTIONS: NavItem[] = [
  {
    to: "/onboardings/new",
    label: "Novo tenant",
    icon: Plus,
    roles: ["platform-master", "platform-admin", "platform-operations", "dev-on-call"],
  },
];

const NAV_SYSTEM: NavItem[] = [
  { to: "/health", label: "Healthcheck", icon: Activity },
  {
    to: "/admin/ai-credentials",
    label: "Credenciais IA",
    icon: KeyRound,
    roles: ["platform-master"],
  },
  {
    to: "/admin/ai-invocations",
    label: "Invocações IA",
    icon: Activity,
    roles: ["platform-master"],
  },
  {
    to: "/admin/saga-variables",
    label: "Variáveis Globais",
    icon: Globe,
    roles: ["platform-master", "platform-admin"],
  },
];

export function Sidebar() {
  const { user, hasAnyRole } = useAuthUser();

  const renderItem = (item: NavItem) => {
    if (item.roles && (!user || !hasAnyRole(...item.roles))) return null;
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-2.5 px-3 py-1.5 rounded text-sm font-medium transition-colors",
            isActive
              ? "bg-sidebar-2 text-sidebar-fg-strong"
              : "text-sidebar-fg hover:bg-sidebar-2 hover:text-sidebar-fg-strong",
          )
        }
      >
        <Icon size={16} strokeWidth={1.75} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside
      className="w-60 bg-sidebar text-sidebar-fg flex flex-col border-r border-sidebar-border"
      aria-label="Navegação principal"
    >
      {/* Branding */}
      <div className="px-4 h-12 flex items-center gap-2 border-b border-sidebar-border">
        <div className="w-7 h-7 rounded bg-primary text-primary-foreground grid place-items-center font-bold text-sm">
          W
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold text-sidebar-fg-strong">Wynk Orchestrator</span>
          <span className="text-[10px] text-sidebar-fg-muted font-mono-tight">WOS · MVP</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-6 overflow-y-auto">
        <div>
          <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-fg-muted">
            Principal
          </p>
          <div className="space-y-0.5">{NAV_MAIN.map(renderItem)}</div>
        </div>

        <div>
          <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-fg-muted">
            Ações
          </p>
          <div className="space-y-0.5">{NAV_ACTIONS.map(renderItem)}</div>
        </div>

        <div>
          <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-fg-muted">
            Sistema
          </p>
          <div className="space-y-0.5">{NAV_SYSTEM.map(renderItem)}</div>
        </div>
      </nav>

      {/* Footer com versão */}
      <div className="px-4 py-3 border-t border-sidebar-border">
        <p className="text-[10px] text-sidebar-fg-muted font-mono-tight">
          v0.1.0 · pre-MVP
        </p>
      </div>
    </aside>
  );
}
